import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { addAnItem } from "../../store/cart-actions";
import { toast } from "react-toastify";

const WebSocketListener = () => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const dispatch = useDispatch();
  const [products, setProducts] = useState([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [config, setConfig] = useState(null);

  // Use refs to avoid closure issues in WebSocket callbacks
  const productsRef = useRef([]);
  const productsLoadedRef = useRef(false);

  // Queue to store messages that arrive before products are loaded
  const messageQueueRef = useRef([]);

  // Process queued messages once products are loaded
  useEffect(() => {
    if (productsLoaded && messageQueueRef.current.length > 0) {
      console.log("Processing", messageQueueRef.current.length, "queued messages...");
      messageQueueRef.current.forEach(message => {
        processWebSocketMessage(message);
      });
      messageQueueRef.current = [];
    }
  }, [productsLoaded, products]);

  // Function to process WebSocket messages
  const processWebSocketMessage = (message) => {
    console.log("Processing WebSocket message:", message);

    // Get expected channel from config, fallback to default
    const expectedChannel = config?.siteSpecificAttributes?.websocketChannel || "StaticMoveoSocket";

    // Check if this is the expected message format with configured channel
    if (
      message.channel === expectedChannel &&
      message.data &&
      message.data.id
    ) {
      const productId = message.data.id;
      console.log("Looking for product with ID:", productId, "Type:", typeof productId, "Length:", productId?.length);

      // Trim whitespace from productId to handle potential space issues
      const cleanProductId = productId ? productId.toString().trim() : "";
      console.log("Clean product ID:", cleanProductId);

      // Use the ref to get current products array
      const currentProducts = productsRef.current;
      console.log("Current products array length:", currentProducts.length);

      if (currentProducts.length > 0) {
        console.log("Available product IDs (first 10):", currentProducts.slice(0, 10).map(p => `"${p.id}"`));
        console.log("Last few product IDs:", currentProducts.slice(-5).map(p => `"${p.id}"`));

        // Check if item-67 specifically exists
        const item67Exists = currentProducts.some(p => p.id === "item-67");
        console.log("Does item-67 exist in products?", item67Exists);

        // Find the product in the loaded products data with trimmed comparison
        const product = currentProducts.find((p) => p.id.trim() === cleanProductId);

        if (product) {
          // Add the product to cart using Redux action
          dispatch(addAnItem(product));

          // Show toast notification
          toast(`${product.name} added to cart via WebSocket`, {
            position: toast.POSITION.BOTTOM_RIGHT,
          });

          // Track analytics if available
          if (window.analytics) {
            window.analytics.track("WebSocket Add to Cart", {
              title: "WebSocket Add to cart: " + product.name,
              productId: productId,
              source: "websocket",
            });
          }

          console.log("Product added to cart via WebSocket:", product.name);
        } else {
          console.warn("Product not found with ID:", productId);
          toast.error(`Product with ID ${productId} not found`, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      } else {
        console.error("Products array is still empty when processing message!");
      }
    }
  };

  // Load config and products data on component mount
  useEffect(() => {
    const loadConfigAndProducts = async () => {
      try {
        // Load site configuration
        console.log("Loading site configuration...");
        const configResponse = await fetch("/json/retail/site.config.json");
        if (!configResponse.ok) {
          throw new Error(`Failed to load config: ${configResponse.status}`);
        }
        const configData = await configResponse.json();
        setConfig(configData);
        console.log("Site config loaded:", configData.siteSpecificAttributes);

        // Load products data
        console.log("Fetching products from /json/retail/response/products.json...");
        const response = await fetch("/json/retail/response/products.json");
        console.log("Response status:", response.status, response.statusText);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const productsData = await response.json();
        console.log("Products data loaded:", productsData.length, "items");
        console.log("First 3 products:", productsData.slice(0, 3).map(p => ({ id: p.id, name: p.name })));

        // Check if item-67 is in the loaded data
        const item67 = productsData.find(p => p.id === "item-67");
        console.log("Item-67 found in loaded data:", item67 ? item67.name : "NOT FOUND");

        setProducts(productsData);
        setProductsLoaded(true);

        // Update refs for WebSocket callbacks
        productsRef.current = productsData;
        productsLoadedRef.current = true;

        console.log("Products loaded and ready for WebSocket messages");
      } catch (error) {
        console.error("Failed to load configuration or products data:", error);
        console.error("Error details:", error.message);
      }
    };

    loadConfigAndProducts();
  }, []);

  const connectWebSocket = () => {
    try {
      // Get WebSocket URL from config, fallback to default
      const baseWsUrl = config?.siteSpecificAttributes?.websocketURL || "ws://localhost:8090";
      const wsUrl = `${baseWsUrl}?username=static-website-user`;

      console.log("Connecting to WebSocket:", wsUrl);
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log("WebSocket connected to static website listener");
      };

      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log("WebSocket message received:", message);

          // Use ref to check if products are loaded (avoids closure issues)
          if (productsLoadedRef.current) {
            console.log("Products are loaded, processing message immediately");
            processWebSocketMessage(message);
          } else {
            // Queue the message to be processed when products are loaded
            console.log("Products not loaded yet, queueing message for later processing");
            messageQueueRef.current.push(message);
          }
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log("WebSocket connection closed:", event.code, event.reason);

        // Attempt to reconnect after 3 seconds if connection was not closed intentionally
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log("Attempting to reconnect WebSocket...");
            connectWebSocket();
          }, 3000);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error("WebSocket error:", error);
      };
    } catch (error) {
      console.error("Failed to create WebSocket connection:", error);
    }
  };

  // Initialize WebSocket connection when config is loaded
  useEffect(() => {
    if (config) {
      console.log("Config loaded, initializing WebSocket connection...");
      connectWebSocket();
    }

    // Cleanup function
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, "Component unmounting");
      }
    };
  }, [config]);

  // This component doesn't render anything visible
  return null;
};

export default WebSocketListener;
